if (!customElements.get('shoppable-ugc-carousel')) {
  class ShoppableUgcCarousel extends HTMLElement {
    get SELECTORS() {
      return {
        swiper: '.shoppable-ugc-videos__swiper',
        slide: '.swiper-slide',
        videoWrapper: '.shoppable-ugc-videos__video-wrapper',
        videoTemplate: 'template[data-video-template]',
        slideVideo: '[data-slide-video]',
        soundToggle: '[data-sound-toggle]',
        iconMuted: '[data-icon-muted]',
        iconUnmuted: '[data-icon-unmuted]',
      };
    }

    get CLASSES() {
      return {
        muted: 'is-muted',
        slideActive: 'swiper-slide-active',
      };
    }

    /**
     * Number of slides on each side of the active slide that should keep a
     * mounted <video> element (metadata preloaded, ready to play on swipe).
     * Everything beyond this window is unmounted and frees its media pipeline.
     * Keeping this at 1 means at most 3 live <video> elements — well below
     * iOS Safari's concurrent media element limit (~16).
     */
    get MOUNT_WINDOW() {
      return 1;
    }

    constructor() {
      super();
      this.isMuted = true;
      this.swiperInstance = null;
      this._activeVideo = null;
      this._io = null;
      this._started = false;
      this._slideClickHandler = this._onSlideClick.bind(this);
      this._soundToggleHandler = this._onSoundToggle.bind(this);
      this._mobileMqHandler = this._onMobileMqChange.bind(this);
      this._mobileMq = null;
    }

    /**
     * Connected callback.
     * @returns {Void}
     */
    connectedCallback() {
      this.swiperEl = this.querySelector(this.SELECTORS.swiper);
      if (!this.swiperEl) return;

      this._bindSoundToggles();
      this._initSwiper();
    }

    /**
     * Disconnected callback.
     * @returns {Void}
     */
    disconnectedCallback() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (this._mobileMq) {
        this._mobileMq.removeEventListener('change', this._mobileMqHandler);
        this._mobileMq = null;
      }
      if (this.swiperEl) {
        this.swiperEl.removeEventListener('click', this._slideClickHandler);
      }
      this.querySelectorAll(this.SELECTORS.soundToggle).forEach((btn) => {
        btn.removeEventListener('click', this._soundToggleHandler);
      });
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    /**
     * Initialize Swiper slider.
     * @returns {Void}
     */
    _initSwiper() {
      const init = () => {
        if (this._started) return;
        if (typeof Swiper === 'undefined') return;
        this._started = true;

        this._createSwiper();
        this.swiperEl.addEventListener('click', this._slideClickHandler);

        // The mobile row layout changes centering and offsets, which Swiper
        // can't apply to a looped slider through breakpoints. Rebuild it
        // whenever the viewport crosses the mobile breakpoint instead.
        if (this.hasAttribute('data-mobile-row')) {
          this._mobileMq = window.matchMedia('(max-width: 749px)');
          this._mobileMq.addEventListener('change', this._mobileMqHandler);
        }
      };

      // Swiper may not have evaluated yet when the section enters the viewport.
      // Retry a bounded number of times before giving up so we never spin
      // forever if the bundle fails to load.
      let swiperTries = 0;
      const start = () => {
        if (typeof Swiper !== 'undefined') {
          init();
          return;
        }
        if (swiperTries++ < 50) setTimeout(start, 100);
      };

      // Defer all video mounting and autoplay until the section nears the
      // viewport. This section can sit far below the fold, and mounting/
      // playing a video on page load streams megabytes that compete with the
      // page's LCP. Until the observer fires, nothing is mounted, so zero
      // video bytes are fetched.
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries, obs) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              obs.disconnect();
              this._io = null;
              start();
            }
          },
          { rootMargin: '200px 0px' }
        );
        this._io.observe(this);
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => setTimeout(start, 100));
      } else {
        setTimeout(start, 100);
      }
    }

    /**
     * Create the Swiper instance for the current viewport.
     * @returns {void}
     */
    _createSwiper() {
      const slideCount = this.swiperEl.querySelectorAll(this.SELECTORS.slide).length;

      // Mobile "row" layout: equal size slides aligned to the left edge,
      // so no centering, no scale compensation and a real positive gap.
      const isMobileRow =
        this.hasAttribute('data-mobile-row') && window.matchMedia('(max-width: 749px)').matches;
      const mobileOffset = parseInt(this.dataset.mobileOffset, 10) || 0;
      const layoutParams = isMobileRow
        ? {
            centeredSlides: false,
            spaceBetween: parseInt(this.dataset.mobileGap, 10) || 0,
            slidesOffsetBefore: mobileOffset,
            slidesOffsetAfter: mobileOffset,
          }
        : {
            centeredSlides: true,
            // Inactive slides are scaled to ~0.728 (388/533), which leaves
            // ~40.8px of empty space on each side of their 300px layout slot.
            // We use *negative* spaceBetween so slide slots overlap into that
            // dead space, producing the intended visual gap between videos.
            //   visible_gap ≈ spaceBetween + 41
            // So desktop: -20 → ~21px gap, mobile: -29 → ~12px gap.
            spaceBetween: -20,
            breakpoints: {
              0: { spaceBetween: -29 },
              750: { spaceBetween: -25 },
              989: { spaceBetween: -20 },
            },
          };

      this.swiperInstance = new Swiper(this.swiperEl, {
        slidesPerView: 'auto',
        loop: slideCount >= 3,
        autoHeight: false,
        loopAdditionalSlides: 2,
        speed: 600,
        ...layoutParams,
        // Native touch is smooth and tracks the finger in real time.
        // The old custom touchstart/touchend handlers only fired on release,
        // which is why swiping felt laggy.
        allowTouchMove: true,
        // Swiper prevents click events that are actually part of a swipe,
        // so product cards won't accidentally open while dragging.
        preventClicks: true,
        preventClicksPropagation: true,
        on: {
          init: () => {
            this._updateVideoMounts();
            this._activateSlideVideo();
            this._setSwiperMinHeight();
          },
          resize: () => {
            this._setSwiperMinHeight();
          },
          slideChange: () => {
            this._updateVideoMounts();
            this._activateSlideVideo();
          },
          slideChangeTransitionEnd: () => {
            this._activateSlideVideo();
          },
          beforeLoopFix: () => {
            this.swiperEl.classList.add('swiper--no-transition');
          },
          loopFix: () => {
            requestAnimationFrame(() => {
              this.swiperEl.classList.remove('swiper--no-transition');
            });
          },
        },
      });
    }

    /**
     * Rebuild Swiper when the viewport crosses the mobile breakpoint.
     * @returns {void}
     */
    _onMobileMqChange() {
      if (!this.swiperInstance) return;

      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
      this.swiperEl.style.minHeight = '';
      this._createSwiper();
    }

    /**
     * Bind sound toggles.
     * @returns {void}
     */
    _bindSoundToggles() {
      this.querySelectorAll(this.SELECTORS.soundToggle).forEach((btn) => {
        btn.addEventListener('click', this._soundToggleHandler);
      });
    }

    /**
     * Mount a <video> element for a given slide by cloning its <template>.
     * No-op if the slide already has a mounted video or has no template.
     * @param {HTMLElement} slide
     * @returns {HTMLVideoElement|null}
     */
    _mountVideo(slide) {
      const wrapper = slide.querySelector(this.SELECTORS.videoWrapper);
      if (!wrapper) return null;

      const existing = wrapper.querySelector(this.SELECTORS.slideVideo);
      if (existing) return existing;

      const tpl = wrapper.querySelector(this.SELECTORS.videoTemplate);
      if (!tpl) return null;

      const fragment = tpl.content.cloneNode(true);
      const video = fragment.querySelector('video');
      if (!video) return null;

      video.muted = this.isMuted;

      // Insert before the sound button so stacking is consistent.
      const soundBtn = wrapper.querySelector(this.SELECTORS.soundToggle);
      if (soundBtn) wrapper.insertBefore(fragment, soundBtn);
      else wrapper.appendChild(fragment);

      return video;
    }

    /**
     * Unmount the <video> element from a slide, fully releasing its
     * media pipeline (critical on iOS Safari).
     * @param {HTMLElement} slide
     * @returns {void}
     */
    _unmountVideo(slide) {
      const wrapper = slide.querySelector(this.SELECTORS.videoWrapper);
      if (!wrapper) return;

      const video = wrapper.querySelector(this.SELECTORS.slideVideo);
      if (!video) return;

      if (this._activeVideo === video) this._activeVideo = null;

      try {
        video.pause();
        // Remove all <source> children, drop any src attribute, then call
        // load() so the browser tears down the decoder and frees memory.
        while (video.firstChild) video.removeChild(video.firstChild);
        video.removeAttribute('src');
        video.load();
      } catch (e) {
        /* swallow — element may already be detached */
      }

      video.remove();
    }

    /**
     * Mount videos for slides within MOUNT_WINDOW of the active slide and
     * unmount everything else. Runs on init and after every slide change.
     * @returns {void}
     */
    _updateVideoMounts() {
      if (!this.swiperInstance) return;

      const slides = this.swiperInstance.slides;
      if (!slides || !slides.length) return;

      const activeIdx = this.swiperInstance.activeIndex;
      const windowSize = this.MOUNT_WINDOW;

      for (let i = 0; i < slides.length; i++) {
        const distance = Math.abs(i - activeIdx);
        if (distance <= windowSize) {
          this._mountVideo(slides[i]);
        } else {
          this._unmountVideo(slides[i]);
        }
      }
    }

    /**
     * On sound toggle button click.
     * @param {Event} e
     * @returns {void}
     */
    _onSoundToggle(e) {
      e.stopPropagation();
      this.isMuted = !this.isMuted;

      // Only mounted videos exist in the DOM — no need to iterate templates.
      this.querySelectorAll(this.SELECTORS.slideVideo).forEach((video) => {
        video.muted = this.isMuted;
      });

      this.querySelectorAll(this.SELECTORS.soundToggle).forEach((btn) => {
        btn.classList.toggle(this.CLASSES.muted, this.isMuted);
      });
    }

    /**
     * On slide click.
     * @param {Event} e
     * @returns {void}
     */
    _onSlideClick(e) {
      if (e.target.closest(this.SELECTORS.soundToggle)) return;
      // Row layout keeps product cards clickable on every visible slide.
      if (e.target.closest('.shoppable-ugc-videos__product-card')) return;

      const slide = e.target.closest(this.SELECTORS.slide);
      if (!slide || !this.swiperInstance) return;

      // Inactive slide — navigate to it.
      if (!slide.classList.contains(this.CLASSES.slideActive)) {
        const realIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
        if (!isNaN(realIndex)) this.swiperInstance.slideToLoop(realIndex);
        return;
      }

      // Active slide — toggle play/pause on the mounted video.
      const video = slide.querySelector(this.SELECTORS.slideVideo);
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    /**
     * Activate video in the currently active slide.
     * @returns {void}
     */
    _activateSlideVideo() {
      if (!this.swiperInstance) return;

      const activeSlide = this.swiperInstance.slides[this.swiperInstance.activeIndex];
      if (!activeSlide) return;

      const video = activeSlide.querySelector(this.SELECTORS.slideVideo);

      if (video && video === this._activeVideo && !video.paused) return;

      // Pause the previously active video. We deliberately do NOT reset
      // currentTime — re-seeking to 0 forces a re-buffer on every swipe,
      // which is expensive on slow mobile networks.
      if (this._activeVideo && this._activeVideo !== video) {
        try { this._activeVideo.pause(); } catch (e) { /* noop */ }
      }

      if (video) {
        video.muted = this.isMuted;
        this._activeVideo = video;
        const p = video.play();
        if (p) p.catch(() => {});
      } else {
        this._activeVideo = null;
      }
    }

    /**
     * Set swiper min-height to the active slide height.
     * @returns {void}
     */
    _setSwiperMinHeight() {
      // Clear first so a previous (taller) layout doesn't leak into the measurement.
      this.swiperEl.style.minHeight = '';
      const activeSlide = this.swiperEl.querySelector(`.${this.CLASSES.slideActive}`);
      if (!activeSlide) return;
      this.swiperEl.style.minHeight = `${activeSlide.offsetHeight}px`;
    }
  }

  customElements.define('shoppable-ugc-carousel', ShoppableUgcCarousel);
}
